import React, {useCallback} from 'react';
import {Button, IconButton, TableCell, Tooltip} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import {BUILTIN_COLORS, KeyVault} from "../../api/Model";
import {useKeyVaultStore} from "../../api/KeyVaultStore";
import {Page, StorePageCount} from "../../components/page/Page";
import {LoadingData} from "../../components/loading-data/LoadingData";
import {TableColumnData} from "../../components/data-table/TableColumnData";
import {DataTable} from "../../components/data-table/DataTable";
import {KeyVaultChip} from "../../components/key-vault-chip/KeyVaultChip";
import {useDeleteAction} from "../../util/actions/DeleteAction";
import {useFormDialogAction} from "../../components/form-dialog/FormDialogProvider";
import {KeyVaultDialog} from "./KeyVaultDialog";

function ActionColumn({keyVault}: { keyVault: KeyVault }) {
    const context = React.useContext(KeyVaultActionContext);
    if (!context) throw new Error('KeyVaultActionContext not found');
    const {
        onEditVault: editVault,
        onDeleteVault: deleteVault,
    } = context;

    const handleEdit = useCallback(() => {
        editVault(keyVault);
    }, [editVault, keyVault]);

    const handleDelete = useCallback(() => {
        deleteVault(keyVault);
    }, [deleteVault, keyVault]);

    return (
        <TableCell align="right">
            <Tooltip title="Edit">
                <IconButton
                    color="primary"
                    onClick={handleEdit}
                >
                    <EditIcon/>
                </IconButton>
            </Tooltip>
            <Tooltip title="Delete">
                <IconButton
                    color="error"
                    onClick={handleDelete}
                >
                    <DeleteIcon/>
                </IconButton>
            </Tooltip>
        </TableCell>
    );
}

const columns: TableColumnData<KeyVault>[] = [
    {
        key: 'name',
        header: 'Name',
        cell: keyVault => <TableCell><KeyVaultChip keyVault={keyVault}/></TableCell>,
    },
    {
        key: 'actions',
        header: 'Actions',
        width: '12em',
        align: 'right',
        cell: keyVault => <ActionColumn keyVault={keyVault}/>,
    },
];

type KeyVaultActions = {
    onEditVault: (vault: KeyVault) => void;
    onDeleteVault: (vault: KeyVault) => void;
}

const KeyVaultActionContext = React.createContext<KeyVaultActions | undefined>(undefined);

function KeyVaultsPage() {
    const {
        remoteValue: keyVaults,
        createKeyVault,
        updateKeyVault,
        deleteKeyVault,
    } = useKeyVaultStore();

    const handleCreateVault = useFormDialogAction({
        title: 'Create Key Vault',
        description: 'Enter the details for the new key vault.',
        submitButtonText: 'Create',
        renderForm: KeyVaultDialog,
        action: (openDialog) => openDialog({
            initialValue: {
                name: '',
                color: BUILTIN_COLORS.DEFAULT,
            },
            onSubmit: createKeyVault,
            successMessage: 'Created new key vault',
            errorMessage: 'Failed to create a key vault. Please try again later.',
        })
    }, [createKeyVault]);

    const handleUpdateVault = useFormDialogAction({
        title: 'Edit Key Vault',
        description: 'Update the key vault information below.',
        submitButtonText: 'Update',
        renderForm: KeyVaultDialog,
        action: (openDialog, keyVault: KeyVault) => openDialog({
            initialValue: {
                name: keyVault.name,
                color: keyVault.color,
            },
            onSubmit: form => updateKeyVault(keyVault, form),
            successMessage: 'Updated key vault',
            errorMessage: 'Failed to update key vault. Please try again later.',
        })
    }, [updateKeyVault]);

    const handleDeleteVault = useDeleteAction({
        description: 'Are you sure you want to delete this key vault? This action cannot be undone.',
        action: deleteKeyVault,
        successMessage: keyVault => `Deleted key vault ${keyVault.name}`,
        failureMessage: keyVault => `Failed to delete key vault ${keyVault.name}`,
    }, [deleteKeyVault]);

    return (
        <Page
            header="Key Vaults"
            counter={<StorePageCount value={keyVaults}/>}
            description="Manage key vaults for storing and organizing keys."
            actions={
                <Button
                    variant="contained"
                    color="primary"
                    startIcon={<AddIcon/>}
                    onClick={handleCreateVault}
                >
                    Add Key Vault
                </Button>
            }
        >
            <LoadingData
                value={keyVaults}
                emptyMessage="No key vaults found."
                defaultErrorMessage="Failed to load key vaults data. Please try again later."
            >{resolved => (
                <KeyVaultActionContext.Provider value={{
                    onEditVault: handleUpdateVault,
                    onDeleteVault: handleDeleteVault,
                }}>
                    <DataTable
                        columns={columns}
                        rows={resolved}
                        id={keyVault => keyVault.id}
                    />
                </KeyVaultActionContext.Provider>
            )}</LoadingData>
        </Page>
    );
}

export default KeyVaultsPage;
